/**
 * Per-stream syllabus order keys. A "stream" is the (orgLevel, examLevel)
 * pair that identifies a cohort within a tenant: e.g. Sec 1 IB is one stream,
 * Sec 1 G2 is another. Topic order on a Syllabus is stored in a Map keyed by
 * the canonical string produced by composeStreamKey.
 *
 * IP streams take an optional third segment, the school cluster
 * (`v1:secondary_1|IP|hci`). Every IP school covers the same content over four
 * years, but schools differ in which year a topic lands and in what order it
 * comes within that year, so the cluster selects *which stored order applies*
 * — it is a sequencing dimension only, never content membership. Clusters are
 * IP-only by design: a G3 student at a school that runs an IP cluster still
 * follows the G3 sequence, because the cluster describes that school's IP
 * syllabus and nothing else. See `shared/ipClusters.ts` for the registry.
 *
 * Versioned (`v1:`) so future curriculum-version dimensions can extend the
 * key without rewriting every stored Map. The cluster segment is additive
 * *within* v1 on purpose rather than a `v2:`: base keys keep their exact byte
 * form, so stored Map keys, grandfather fingerprints and order caches all
 * survive the change untouched. Mongoose Map keys cannot contain `.` or `$`;
 * the regex below rejects both, plus stray separators.
 */
import { EXAM_LEVELS, type ExamLevel } from './examLevels.js';
import { ORG_LEVELS, type OrgLevel } from './orgLevels.js';

/** Stream identity: only the dimensions that affect topic order. */
export interface StreamIdentity {
  orgLevel: OrgLevel;
  examLevel: ExamLevel;
  /**
   * IP school-cluster id; null or absent for the base (cluster-less) stream.
   * Checked against the grammar only — this module is registry-blind, so a
   * well-formed but unregistered id composes fine. Call sites sanitize with
   * `isIpClusterId` and fall back to the base key.
   */
  cluster?: string | null;
}

/** Parsed key with the version prefix retained for future-proofing. */
export interface ParsedStreamKey extends StreamIdentity {
  version: 'v1';
  /** null on a base key — never undefined, so callers can compare directly. */
  cluster: string | null;
}

const VERSION_PREFIX = 'v1';

/** The only exam level whose keys may carry a cluster segment. */
const CLUSTERED_EXAM_LEVEL: ExamLevel = 'IP';

/**
 * Anchored on both ends. The orgLevel half allows `primary_1..6` and
 * `secondary_1..4`; the examLevel half allows the seven canonical exam
 * levels; the optional third half is a cluster id. Bare-pipe separator inside
 * a half is forbidden by the character classes. The examLevel-must-be-IP rule
 * for the third half is enforced after the match, not in the grammar, so the
 * error can say why.
 */
const STREAM_KEY_REGEX =
  /^v1:(primary_[1-6]|secondary_[1-4])\|(PSLE|G1|G2|G3|IP|IB|A-Level)(?:\|([a-z][a-z0-9_-]{1,31}))?$/;

/**
 * The cluster segment on its own, for checking an id before it ever reaches a
 * key. `shared/ipClusters.ts` re-uses this as `IP_CLUSTER_ID_REGEX` so the
 * registry and the key grammar cannot disagree; a test pins it against the
 * third group of `STREAM_KEY_REGEX` above.
 */
export const STREAM_CLUSTER_SEGMENT_REGEX = /^[a-z][a-z0-9_-]{1,31}$/;

const ORG_LEVEL_SET = new Set<string>(ORG_LEVELS);
const EXAM_LEVEL_SET = new Set<string>(EXAM_LEVELS);

/**
 * Build the canonical stream key. Throws on inputs not in the canonical
 * enums so a typo at the callsite fails loudly instead of writing a
 * never-readable Map entry. A nullish `cluster` yields the base key,
 * byte-identical to what this function returned before clusters existed.
 */
export function composeStreamKey(identity: StreamIdentity): string {
  if (!ORG_LEVEL_SET.has(identity.orgLevel)) {
    throw new Error(`Unknown orgLevel: ${identity.orgLevel}`);
  }
  if (!EXAM_LEVEL_SET.has(identity.examLevel)) {
    throw new Error(`Unknown examLevel: ${identity.examLevel}`);
  }
  const baseKey = `${VERSION_PREFIX}:${identity.orgLevel}|${identity.examLevel}`;
  const cluster = identity.cluster;
  if (cluster === null || cluster === undefined) {
    return baseKey;
  }
  if (identity.examLevel !== CLUSTERED_EXAM_LEVEL) {
    throw new Error(
      `Cluster "${cluster}" is only valid on ${CLUSTERED_EXAM_LEVEL} streams, got examLevel: ${identity.examLevel}`
    );
  }
  if (!STREAM_CLUSTER_SEGMENT_REGEX.test(cluster)) {
    throw new Error(`Malformed cluster id: ${cluster}`);
  }
  return `${baseKey}|${cluster}`;
}

/**
 * The single matcher behind both `parseStreamKey` and `isStreamKey`, so the
 * two can never disagree about what a valid key is.
 */
function matchStreamKey(key: string): ParsedStreamKey | null {
  const match = STREAM_KEY_REGEX.exec(key);
  if (!match) {
    return null;
  }
  const orgLevel = match[1] as OrgLevel;
  const examLevel = match[2] as ExamLevel;
  const cluster = match[3] ?? null;
  if (cluster !== null && examLevel !== CLUSTERED_EXAM_LEVEL) {
    return null;
  }
  return { version: 'v1', orgLevel, examLevel, cluster };
}

/**
 * Parse a stream key. Throws on shape mismatch (regex anchored both ends),
 * unknown enum values, or a cluster segment on a non-IP stream. The only
 * callsite that decomposes a key.
 */
export function parseStreamKey(key: string): ParsedStreamKey {
  const parsed = matchStreamKey(key);
  if (!parsed) {
    throw new Error(`Malformed stream key: ${key}`);
  }
  return parsed;
}

/**
 * The cluster-less key for the same stream. Order resolution falls back to it
 * whenever a cluster has no stored order of its own, so a registered cluster
 * awaiting its migration behaves exactly like the base stream rather than
 * like an empty one. Returns the input unchanged for a key that has no
 * cluster segment; throws on a malformed key, like `parseStreamKey`.
 */
export function baseStreamKey(key: string): string {
  const { orgLevel, examLevel } = parseStreamKey(key);
  return composeStreamKey({ orgLevel, examLevel });
}

/**
 * URL-segment encoded form, for any future use as a path parameter.
 * Endpoints that take a stream key should prefer the request body to avoid
 * proxy URL-encoding fragility, but this is here so we never have to
 * hand-encode at a callsite.
 */
export function encodeStreamKeyForPath(key: string): string {
  // Validate before encoding so callers get the same loud failure as compose.
  parseStreamKey(key);
  return encodeURIComponent(key);
}

/** Returns true iff key has the canonical shape. Cheap, no throw. */
export function isStreamKey(key: string): boolean {
  return matchStreamKey(key) !== null;
}
